import { usePlayerProvider } from 'providers/PlayerProvider';
import { useStoreState } from 'providers/StoreStateContext';
import { SyntheticEvent, useCallback, useEffect, useRef, useState } from 'react';
import { IVideoArtistResponse } from 'services/video/types';

export interface IVideoPlayerMeta {
  title?: string;
  artists?: IVideoArtistResponse[];
}

export const useVideoPlayer = (
  playbackUrl: string | null,
  video?: IVideoPlayerMeta
) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const wasPlayingBeforeDragRef = useRef(false);

  const { setIsVideoPlaying: setIsPlaying, isVideoPlaying: isPlaying } =
    usePlayerProvider();
  const {
    videoVolume: volume,
    setVideoVolume: setVolume,
    setPlayerTrackId,
  } = useStoreState();

  const [isBuffering, setIsBuffering] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [aspectRatio, setAspectRatio] = useState(16 / 9);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.volume = volume;
    }
  }, [volume]);

  useEffect(() => {
    if (!playbackUrl) {
      setIsPlaying(false);
      setCurrentTime(0);
      setDuration(0);
    }
  }, [playbackUrl, setIsPlaying]);

  useEffect(() => {
    const element = videoRef.current;
    if (!element || !playbackUrl) {
      return;
    }

    if (isPlaying) {
      if (element.paused) {
        element.play().catch(err => {
          if (err.name !== 'AbortError') {
            setIsPlaying(false);
          }
        });
      }
    } else if (!element.paused) {
      element.pause();
    }
  }, [isPlaying, playbackUrl, setIsPlaying]);

  useEffect(() => {
    return () => {
      setIsPlaying(false);
    };
  }, [setIsPlaying]);

  useEffect(() => {
    if (!('mediaSession' in navigator) || !video?.title) {
      return;
    }

    navigator.mediaSession.metadata = new MediaMetadata({
      title: video.title,
      artist: Array.from(
        new Set(video.artists?.map(artist => artist.nickname).filter(Boolean) ?? [])
      ).join(', '),
    });

    navigator.mediaSession.setActionHandler('play', () => {
      if (videoRef.current) {
        void videoRef.current.play();
        setIsPlaying(true);
      }
    });

    navigator.mediaSession.setActionHandler('pause', () => {
      if (videoRef.current) {
        videoRef.current.pause();
        setIsPlaying(false);
      }
    });

    navigator.mediaSession.setActionHandler('seekto', details => {
      if (
        videoRef.current &&
        details.seekTime !== undefined &&
        details.seekTime !== null
      ) {
        videoRef.current.currentTime = details.seekTime;
        setCurrentTime(details.seekTime);
      }
    });

    return () => {
      if ('mediaSession' in navigator) {
        navigator.mediaSession.metadata = null;
        navigator.mediaSession.setActionHandler('play', null);
        navigator.mediaSession.setActionHandler('pause', null);
        navigator.mediaSession.setActionHandler('seekto', null);
      }
    };
  }, [video, setIsPlaying]);

  const handleLoadedMetadata = (event: SyntheticEvent<HTMLVideoElement>) => {
    const element = event.currentTarget;
    setDuration(element.duration);
    if (element.videoWidth && element.videoHeight) {
      setAspectRatio(element.videoWidth / element.videoHeight);
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current && !isDragging) {
      setCurrentTime(videoRef.current.currentTime);
    }
  };

  const playVideo = useCallback(async () => {
    const element = videoRef.current;
    setPlayerTrackId(null);
    setIsPlaying(true);

    if (!element?.src) {
      return;
    }

    setIsBuffering(true);
    try {
      await element.play();
      setIsBuffering(false);
    } catch (err) {
      setIsBuffering(false);
      if (err instanceof Error && err.name !== 'AbortError') {
        setIsPlaying(false);
      }
    }
  }, [setIsPlaying, setPlayerTrackId]);

  const pauseVideo = useCallback(() => {
    videoRef.current?.pause();
    setIsPlaying(false);
    setIsBuffering(false);
  }, [setIsPlaying]);

  const togglePlay = () => {
    if (isPlaying) {
      pauseVideo();
      return;
    }
    void playVideo();
  };

  const handleNativePlay = () => {
    setPlayerTrackId(null);
    setIsPlaying(true);
  };

  const handleNativePause = () => setIsPlaying(false);

  const handleSeekStart = () => {
    if (videoRef.current) {
      wasPlayingBeforeDragRef.current = !videoRef.current.paused;
      videoRef.current.pause();
    }
    setIsDragging(true);
  };

  const handleSeekChange = (newTime: number) => {
    setCurrentTime(newTime);
    if (videoRef.current) {
      videoRef.current.currentTime = newTime;
    }
  };

  const handleSeekEnd = () => {
    setIsDragging(false);
    if (videoRef.current && wasPlayingBeforeDragRef.current) {
      void videoRef.current.play();
      setIsPlaying(true);
    }
  };

  const handleVolumeChange = (newVolume: number) => {
    if (videoRef.current) {
      videoRef.current.volume = newVolume;
    }
    setVolume(newVolume);
  };

  const toggleMute = () => {
    handleVolumeChange(volume > 0 ? 0 : 1);
  };

  const handleEnded = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
    setCurrentTime(0);
    setIsPlaying(false);

    if (document.fullscreenElement) {
      void document.exitFullscreen();
    }
  };

  const toggleFullscreen = () => {
    const element = videoRef.current;
    if (!element) {
      return;
    }

    if (document.fullscreenElement) {
      void document.exitFullscreen();
      return;
    }

    void element.requestFullscreen();
  };

  const handleWaiting = useCallback(() => {
    setIsBuffering(true);
  }, []);

  const handlePlaying = useCallback(() => {
    setIsBuffering(false);
  }, []);

  const handleCanPlay = useCallback(() => {
    setIsBuffering(false);
  }, []);

  return {
    videoRef,
    isPlaying,
    currentTime,
    duration,
    aspectRatio,
    volume,
    isBuffering,
    handleLoadedMetadata,
    handleTimeUpdate,
    togglePlay,
    handleSeekStart,
    handleSeekChange,
    handleSeekEnd,
    handleVolumeChange,
    toggleMute,
    toggleFullscreen,
    handleNativePlay,
    handleNativePause,
    handleEnded,
    handleWaiting,
    handlePlaying,
    handleCanPlay,
  };
};
